import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  Image,
  ActivityIndicator,
} from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { feedbackStrings, commonStrings } from '@/constants/residents/marketplaceStrings';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { BaseModal } from './shared';
import { FeedbackRequest } from '@/store/api';
import { getConditionDisplayText, ListingCondition } from '@/types/residents/marketplaceListing';

const StarIcon = RESIDENTS_SVG_COMPONENTS.MARKETPLACE_STAR;
const StarGrayIcon = RESIDENTS_SVG_COMPONENTS.STAR_GRAY;

interface LeaveFeedbackModalProps {
  visible: boolean;
  feedbackRequest: FeedbackRequest | null;
  onClose: () => void;
  onSubmit: (rating: number, text?: string) => Promise<void>;
  isSubmitting?: boolean;
}

/**
 * Modal for buyers to leave feedback for sellers after a purchase.
 * Shows seller info, star rating selection, and optional review text.
 */
export const LeaveFeedbackModal: React.FC<LeaveFeedbackModalProps> = ({
  visible,
  feedbackRequest,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');

  const handleSubmit = async () => {
    if (rating === 0) return;
    await onSubmit(rating, reviewText.trim() || undefined);
    // Reset state after successful submission
    setRating(0);
    setReviewText('');
  };

  const handleClose = () => {
    // Reset state when closing
    setRating(0);
    setReviewText('');
    onClose();
  };

  const renderInteractiveStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= rating;
      const Icon = isFilled ? StarIcon : StarGrayIcon;
      
      stars.push(
        <TouchableOpacity
          key={i}
          onPress={() => setRating(i)}
          activeOpacity={0.7}
          style={styles.starButton}
          accessibilityRole="button"
          accessibilityLabel={`Rate ${i} star${i > 1 ? 's' : ''}`}
          accessibilityState={{ selected: isFilled }}
        >
          <Icon
            width={34}
            height={34}
          />
        </TouchableOpacity>
      );
    }
    return <View style={styles.starsContainer}>{stars}</View>;
  };

  const footer = (
    <View style={styles.footerContainer}>
      <TouchableOpacity
        style={[
          styles.submitButton,
          (rating === 0 || isSubmitting) && styles.submitButtonDisabled,
        ]}
        onPress={handleSubmit}
        activeOpacity={0.8}
        disabled={rating === 0 || isSubmitting}
      >
        {isSubmitting ? (
          <ActivityIndicator size="small" color={MARKETPLACE_COLORS.black} />
        ) : (
          <Text style={styles.submitButtonText}>{commonStrings.submit}</Text>
        )}
      </TouchableOpacity>
    </View>
  );

  if (!feedbackRequest) {
    return null;
  }

  return (
    <BaseModal
      visible={visible}
      onClose={handleClose}
      showHeader={true}
      footer={footer}
      size="large"
      avoidKeyboard={true}
    >
      <View style={styles.content}>
        {/* Title */}
        <Text style={styles.title}>
          {feedbackStrings.congratsPurchase}
        </Text>

        {/* Seller/Listing Info Card */}
        <View style={styles.infoCard}>
          {/* Listing Image */}
          {feedbackRequest.listing.image ? (
            <Image
              source={{ uri: feedbackRequest.listing.image }}
              style={styles.listingImage}
              resizeMode="cover"
            />
          ) : (
            <View style={[styles.listingImage, styles.listingImagePlaceholder]} />
          )}

          {/* Info Text */}
          <View style={styles.infoText}>
            <Text style={styles.sellerName}>{feedbackRequest.seller.name}</Text>
            <Text style={styles.listingTitle} numberOfLines={2}>
              {feedbackRequest.listing.title}
            </Text>
            <Text style={styles.condition}>
              {getConditionDisplayText(feedbackRequest.listing.condition as ListingCondition)}
            </Text>
          </View>
        </View>

        {/* Star Rating */}
        {renderInteractiveStars()}

        {/* Review Text Input */}
        <View style={styles.inputContainer}>
          <TextInput
            style={styles.textInput}
            placeholder={feedbackStrings.reviewOptional}
            placeholderTextColor={MARKETPLACE_COLORS.textSecondary}
            value={reviewText}
            onChangeText={setReviewText}
            multiline
            maxLength={500}
            textAlignVertical="top"
          />
        </View>
      </View>
    </BaseModal>
  );
};

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 24,
    gap: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    textAlign: 'center',
    lineHeight: 36,
  },
  infoCard: {
    flexDirection: 'row',
    backgroundColor: MARKETPLACE_COLORS.white5Percent,
    borderRadius: 16,
    padding: 16,
    gap: 16,
  },
  listingImage: {
    width: 85,
    height: 85,
    borderRadius: 14,
    borderWidth: 0.45,
    borderColor: MARKETPLACE_COLORS.borderGray,
  },
  listingImagePlaceholder: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
  },
  infoText: {
    flex: 1,
    gap: 2,
  },
  sellerName: {
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  listingTitle: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  condition: {
    fontSize: 12,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  starsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  starButton: {
    padding: 4,
  },
  inputContainer: {
    backgroundColor: MARKETPLACE_COLORS.inputBackground,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.inputBorder,
    borderRadius: 8,
    minHeight: 80,
  },
  textInput: {
    flex: 1,
    fontSize: 14,
    color: MARKETPLACE_COLORS.textPrimary,
    paddingHorizontal: 16,
    paddingVertical: 12,
    lineHeight: 21,
  },
  footerContainer: {
    width: '100%',
  },
  submitButton: {
    width: '100%',
    height: 40,
    backgroundColor: MARKETPLACE_COLORS.accentRed,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.black,
    lineHeight: 18,
  },
});
